import { Money } from '../models/workbench-row.model'

/**
 * The read API wraps almost every field in an envelope rather than returning a bare value:
 *
 *   "cCY2":        { "value": "USD" }
 *   "accruedInterest": { "value": null }
 *   "tradePrice":  { "value": 25.11, "currency": "USD", "quoteType": "Price", "quotationFormat": "Price4" }
 *
 * The grid works in scalars — value getters, comparators, formatters, the set filter, the range sum
 * and the external filters all read `WorkbenchRow.fields` directly — so the envelope is stripped
 * once here, as the row is adapted, instead of in each of those places.
 *
 * An envelope carrying a currency becomes a {@link Money} so the amount keeps its currency for
 * formatting and per-currency summing. Everything else collapses to the bare value. Quote metadata
 * (`quoteType`, `quotationFormat`) is dropped: nothing renders it.
 */

/**
 * Fields that are blobs in their own right and must not be mistaken for an envelope. `aggInfo` is
 * an arbitrary key/value map from Prometheus, so a key literally named `value` would otherwise
 * swallow the rest of the blob.
 */
const RAW_FIELDS = new Set(['aggInfo'])

const isEnvelope = (raw: unknown): raw is Record<string, unknown> =>
  typeof raw === 'object' &&
  raw !== null &&
  !Array.isArray(raw) &&
  'value' in raw

/** Strip one field's envelope, promoting a currency-bearing amount to {@link Money}. */
export function unwrapFieldValue(raw: unknown): unknown {
  if (!isEnvelope(raw)) return raw

  const value = raw['value']
  const currency = raw['currency']

  // Only a numeric amount with a currency is Money. `{ value: 'USD' }` is a plain text field that
  // happens to hold a currency code, and `{ value: -4167 }` is a quantity — neither gets a symbol.
  if (
    typeof value === 'number' &&
    typeof currency === 'string' &&
    currency !== ''
  ) {
    return { value, currency } as Money
  }

  return value
}

/** Strip the envelope from every field of one response row. */
export function unwrapFields(
  row: Record<string, unknown>,
): Record<string, unknown> {
  const fields: Record<string, unknown> = {}
  Object.keys(row).forEach(key => {
    fields[key] = RAW_FIELDS.has(key) ? row[key] : unwrapFieldValue(row[key])
  })
  return fields
}
